import React, { createContext, useContext, useState, useCallback, useEffect, Suspense, lazy } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import RecordDetailPage from './RecordDetailPage';
import { useGlobalMetadata } from '../utils/providers';
import { getFieldDependentTable } from '../utils/metadata/tables';
import { parseOverlayTarget, parseRecordTarget, serializeParams, withoutOverlayTarget, withoutRecordTarget } from '../utils/recordTargets';
import { useTransientOverlay } from '../utils/transientOverlays';
import { canonicalDriveFolderUrl } from '../features/docs/drive';
import { useRecordDriveFolder } from '../features/docs/useDriveFiles';
import type { RouteMetadata, TableInfo, UIComponentMetadata } from '../types/metadata';
import './LinkedRecordPanel.css';
import SubmissionPanelBody, { SubmissionPanelHeader } from './SidePanel/submission/SubmissionPanelBody';
import { useRecordNavigator } from '../hooks/useRecordNavigator';

// Imported leads have no metadata detail form; they open in the lead drawer.
// Loaded on demand so the lead-import module stays out of the main bundle.
const LeadDrawer = lazy(() => import('./LeadImport/LeadDrawer'));
const LEAD_TABLE = 'lead_import_row';

interface LinkedRecord {
  tableName: string;
  recordId: string | number;
  displayRecordId?: string | number;
}

interface LinkedRecordPanelContextValue {
  openLinkedRecord: (tableName: string, recordId: string | number, displayRecordId?: string | number) => void;
  /** User-initiated dismissal: also drops the record target from the URL. */
  closeLinkedRecord: () => void;
  /** Hide the modal without touching the URL, for URL-driven state changes. */
  dismissLinkedRecord: () => void;
}

const LinkedRecordPanelContext = createContext<LinkedRecordPanelContextValue>({
  openLinkedRecord: () => {},
  closeLinkedRecord: () => {},
  dismissLinkedRecord: () => {},
});

export const useLinkedRecordPanel = () => useContext(LinkedRecordPanelContext);

/** Record ids are integer primary keys throughout the CRM. */
function isWellFormedId(value: string | number): boolean {
  return /^\d+$/.test(String(value));
}

/** Empty-state body shown instead of a half-rendered detail view. */
function PanelMessage({ title, detail }: { title: string; detail: string }): React.ReactElement {
  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{detail}</div>
    </div>
  );
}

export function LinkedRecordPanelProvider({ children }: { children: React.ReactNode }) {
  const [record, setRecord] = useState<LinkedRecord | null>(null);
  const { data: metadata } = useGlobalMetadata();
  const location = useLocation();
  const navigate = useNavigate();
  const { openRecord } = useRecordNavigator();

  const openLinkedRecord = useCallback((tableName: string, recordId: string | number, displayRecordId?: string | number) => {
    setRecord(prev => (
      prev && prev.tableName === tableName && String(prev.recordId) === String(recordId)
        ? prev
        : { tableName, recordId, displayRecordId }
    ));
  }, []);

  const dismissLinkedRecord = useCallback(() => setRecord(null), []);

  /**
   * Dismiss the modal and, when the URL is what put it there, drop the record
   * target so the address bar matches what is on screen. Unrelated
   * search/filter parameters are preserved.
   */
  const closeLinkedRecord = useCallback(() => {
    setRecord(null);
    const params = new URLSearchParams(location.search);
    // A modal over a page detail (funder/rep) lives in the overlay key. Drop
    // only that, so closing it lands back on the detail page, not its list.
    if (parseOverlayTarget(params)) {
      const next = serializeParams(withoutOverlayTarget(params));
      navigate(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
      return;
    }
    if (!parseRecordTarget(params)) return;
    const next = serializeParams(withoutRecordTarget(params));
    navigate(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useTransientOverlay(!!record);

  /** A lead target with a valid id; anything malformed falls through to the generic messages. */
  const leadId = record?.tableName === LEAD_TABLE && isWellFormedId(record.recordId) ? Number(record.recordId) : null;

  useEffect(() => {
    // The lead drawer closes itself on Escape.
    if (!record || leadId != null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLinkedRecord();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [record, leadId, closeLinkedRecord]);

  // Resolve config for the linked record's table
  const routeData = metadata?.routeData as RouteMetadata | null;
  const detailConfig: UIComponentMetadata | null = record
    ? (routeData?.primaryUiComponentByTableName?.[record.tableName]?.detail || null)
    : null;
  const tableInfo = record ? (metadata?.tableData?.byName?.[record.tableName] as TableInfo | undefined) : undefined;
  const tableLabel = tableInfo?.ui_label || record?.tableName?.replace(/_/g, ' ') || '';
  const submissionId = record?.tableName === 'submission' && isWellFormedId(record.recordId)
    ? Number(record.recordId)
    : 0;
  const { data: driveFolder } = useRecordDriveFolder('submission', submissionId);
  const driveFolderUrl = canonicalDriveFolderUrl(driveFolder);

  // A shared link can name a table that does not exist, an id that is not an
  // id, or a record the viewer cannot read. Each has to say so rather than
  // render an empty detail form.
  const unknownTable = !!record && !tableInfo;
  const malformedId = !!record && !isWellFormedId(record.recordId);
  const canCheckExistence = !!record && !unknownTable && !malformedId && leadId == null;

  /**
   * A URL carries the internal id, but users know these records by their
   * human-facing number (a submission's `submission_number`). Where the table
   * has one, the probe below fetches it so the header reads the same whether
   * the record was clicked or opened from a shared link.
   */
  const displayField = record ? `${record.tableName}_number` : null;
  const hasDisplayField = !!displayField
    && !!metadata?.fieldData?.[record!.tableName]?.byFieldName?.[displayField];

  const existence = useQuery({
    queryKey: ['linked-record-exists', record?.tableName, record ? String(record.recordId) : null],
    enabled: canCheckExistence,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await getFieldDependentTable({
        tableName: record!.tableName,
        schema: tableInfo?.schema_name || 'public',
        selectExpression: hasDisplayField ? `id,${displayField}` : 'id',
        singleRow: true,
        withCount: false,
        limitOffset: { limit: 1, offset: 0 },
        conditions: {
          id: { filterType: 'number', type: 'equals', filter: Number(record!.recordId), colId: 'id' },
        },
      });
      if (error) throw error;
      // With `singleRow`, a hit is the row object and a miss is the empty array
      // it fell back to — which is itself truthy.
      const row = Array.isArray(data) ? null : data as Record<string, unknown> | null;
      return { found: row != null, displayId: (hasDisplayField ? row?.[displayField!] : null) ?? null };
    },
  });

  // Treat "no row returned" as not-found/no-access: RLS filters unreadable rows
  // out rather than raising, so the two are indistinguishable from the client.
  const missingRecord = canCheckExistence && existence.isSuccess && !existence.data.found;
  // A submission renders its own header inside the modal's dark bar. Anything
  // that would render an error body keeps the generic header, so a broken link
  // still says which table it pointed at.
  const isSubmission = submissionId > 0 && !missingRecord && !existence.isError;
  const resolvedDisplayId = record?.displayRecordId
    ?? (existence.data?.displayId as string | number | null | undefined)
    ?? record?.recordId;

  const renderBody = (): React.ReactElement => {
    if (!record) return <></>;
    if (unknownTable) {
      return <PanelMessage title="Record not found" detail={`Unknown table "${record.tableName}".`} />;
    }
    if (malformedId) {
      return <PanelMessage title="Record not found" detail={`"${record.recordId}" is not a valid record id.`} />;
    }
    if (existence.isError) {
      return <PanelMessage title="Could not load record" detail={(existence.error as Error)?.message || 'Unexpected error.'} />;
    }
    if (missingRecord) {
      return (
        <PanelMessage
          title="Record not found"
          detail="It may have been deleted, or you may not have access to it."
        />
      );
    }
    // The existence probe runs alongside the body rather than gating it, so a
    // valid record still opens at its usual speed.
    if (record.tableName === 'submission') {
      return (
        <SubmissionPanelBody
          record={{
            id: record.recordId,
            displayId: resolvedDisplayId,
            table: 'Submissions',
            tableName: 'submission',
            label: `Submission #${resolvedDisplayId}`,
          }}
          onClose={closeLinkedRecord}
        />
      );
    }
    if (detailConfig) {
      return (
        <RecordDetailPage
          recordId={record.recordId}
          displayRecordId={resolvedDisplayId}
          config={detailConfig}
          manualSave
        />
      );
    }
    return <PanelMessage title="No detail view" detail="No detail view is configured for this table." />;
  };

  return (
    <LinkedRecordPanelContext.Provider value={{ openLinkedRecord, closeLinkedRecord, dismissLinkedRecord }}>
      {children}
      {leadId != null && (
        <Suspense fallback={null}>
          <LeadDrawer
            leadId={leadId}
            onClose={closeLinkedRecord}
            onOpenLead={(id) => openRecord({ tableName: LEAD_TABLE, id })}
          />
        </Suspense>
      )}
      {record && leadId == null && (
        <>
          <div className="sky-linked-overlay" onClick={closeLinkedRecord} />
          <div className="sky-linked-panel" role="dialog" aria-modal="true" aria-label={`${tableLabel} record`}>
            <div className={`sky-linked-panel-head${isSubmission ? ' is-rich' : ''}`}>
              <div className="sky-linked-panel-head-top">
                {isSubmission ? (
                  <SubmissionPanelHeader recordId={record.recordId} />
                ) : (
                  <div className="sky-linked-panel-info">
                    <div className="sky-linked-panel-eyebrow">{tableLabel}</div>
                    <div className="sky-linked-panel-id">#{resolvedDisplayId}</div>
                  </div>
                )}
                <div className="sky-linked-panel-actions">
                  {driveFolderUrl && (
                    <a
                      className="sky-linked-panel-drive-link"
                      href={driveFolderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open the merchant folder in Google Drive"
                      aria-label="Open the merchant folder in Google Drive"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="#0F9D58" d="M7.68 2.75 1.12 14.1l3.28 5.68 6.56-11.36-3.28-5.67Z" />
                        <path fill="#4285F4" d="M7.68 2.75h6.56l6.56 11.36h-6.56L7.68 2.75Z" />
                        <path fill="#F4B400" d="m4.4 19.78 3.28-5.67h13.12l-3.28 5.67H4.4Z" />
                      </svg>
                    </a>
                  )}
                  <button className="sky-panel-close" onClick={closeLinkedRecord}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="sky-linked-panel-body">
              {renderBody()}
            </div>
          </div>
        </>
      )}
    </LinkedRecordPanelContext.Provider>
  );
}
